import React, { useEffect, useMemo, useState, useCallback } from 'react';
import '../../styles/flashcards.css';
import '../../styles/prepare-up-home.css';
import '../../styles/prepare-up-flashcards.css';
import APIService from '../../services/APIService';
import ModeEmpty from './ModeEmpty';
import { IconArrowLeft, IconArrowRight, IconCheck, IconClock, IconRefresh } from './icons';
import QuestionList, { useQuestionList } from './QuestionList';
import LearnPanel, { useLearnOpen } from './LearnPanel';
import * as sessionCache from './sessionCache';
import { logFlashcard, scopeIds, conceptsFromText } from './memoryLog';
import { buildPaths, getHubTreeCached } from './paths';
import { Bar, HomeSearch, Loading, ThemeToggle, buildSearchEntries } from './HomeDashboard';

/*
 * FlashcardMode — a dedicated, full-view flashcard player (no chat). The deck
 * is CONTINUOUS: it starts from the session cache / engine and quietly extends
 * itself in the background as you approach the end — new hub content first,
 * then spaced re-review of earlier cards (that's what flashcards are for).
 * Every rating is recorded to memory on the card's hub concept ids.
 *
 * Each card is a question that flips to its answer + explanation. On the
 * answer side "Got it" logs `good` and "Review again" logs `again` — the same
 * ratings the engine already reads — and marks the card in the question list.
 */
const BOOKMARK_KEY = 'pu_fc_bookmarks_v1';
const REVIEW_KEY = 'pu_fc_review_v1';
const loadSet = (k) => { try { return new Set(JSON.parse(localStorage.getItem(k)) || []); } catch { return new Set(); } };
const saveSet = (k, s) => { try { localStorage.setItem(k, JSON.stringify([...s])); } catch { /* private mode */ } };
const cardKey = (c) => String(c.item_id || c.question || '');

// Never show a raw dotted concept id — prettify the leaf into a name.
const pretty = (t) => (/^[a-z0-9_]+(\.[a-z0-9_]+)+$/i.test(String(t))
  ? String(t).split('.').pop().replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  : String(t || ''));

/* The card's answer, split for reading: its first sentence is the answer, the
   rest is the explanation. Nothing is added — it is the card's own text. */
const ABBREV = /(?:e\.g|i\.e|vs|etc|approx|cf)\.$/i;
const splitAnswer = (text) => {
  const t = String(text || '').trim();
  const re = /[.!?](?=\s+[A-Z(“"])/g;
  let m;
  while ((m = re.exec(t))) {
    const head = t.slice(0, m.index + 1);
    if (head.length >= 24 && !ABBREV.test(head)) return [head, t.slice(m.index + 1).trim()];
  }
  return [t, ''];
};

const IconBookmark = ({ size = 18, filled = false }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
  </svg>
);

const IconHand = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9 11V5.5a1.5 1.5 0 0 1 3 0V11" /><path d="M12 10V4.5a1.5 1.5 0 0 1 3 0V11" />
    <path d="M15 10.5V6.5a1.5 1.5 0 0 1 3 0v7.5a6 6 0 0 1-6 6h-1a6 6 0 0 1-4.6-2.2L3.6 14a1.5 1.5 0 0 1 2.3-1.9L9 15V11" />
  </svg>
);

const FlashcardMode = ({
  config, onConfigure,
  onBack, profile, onEditProfile, theme, onToggleTheme, onOpenPath, onOpenHubNode,
}) => {
  const cids = useMemo(() => scopeIds(config), [config]);
  const [deck, setDeck] = useState(null);   // null = loading, [] = empty
  const [err, setErr] = useState('');
  const [i, setI] = useState(0);
  const [phase, setPhase] = useState('front');   // 'front' (question) | 'back' (answer)
  const [dir, setDir] = useState('right');
  const [results, setResults] = useState({});    // card index → 'got' | 'again'
  const [bookmarks, setBookmarks] = useState(() => loadSet(BOOKMARK_KEY));
  const [review, setReview] = useState(() => loadSet(REVIEW_KEY));
  const [listOpen, setListOpen] = useQuestionList();   // the right-hand Questions panel / drawer
  const [hub, setHub] = useState(null);
  const [learnOpen, toggleLearn] = useLearnOpen('flashcards');
  const fetchingRef = React.useRef(false);
  const exhaustedRef = React.useRef(false);   // hub has no NEW cards → re-review mode

  useEffect(() => {
    let live = true;
    getHubTreeCached().then((d) => { if (live) setHub(d.tree || []); }).catch(() => { if (live) setHub([]); });
    return () => { live = false; };
  }, []);
  const searchEntries = useMemo(() => {
    const p = hub ? buildPaths(hub) : null;
    return buildSearchEntries(hub, p ? [...p.roles, ...p.areas] : []);
  }, [hub]);

  // Continuous deck: as the learner nears the end, fetch more cards in the
  // background. New (non-duplicate) content extends the deck; when the hub is
  // exhausted, earlier cards come back for another pass — endless by design.
  const extend = useCallback(async (currentDeck) => {
    if (fetchingRef.current || !cids.length) return;
    fetchingRef.current = true;
    try {
      let fresh = [];
      if (!exhaustedRef.current) {
        const res = await APIService.engineFlashcards({ conceptIds: cids, count: 6 });
        const seen = new Set(currentDeck.map((c) => c.question));
        fresh = (res.cards || []).filter((c) => c.question && !seen.has(c.question));
        if (!fresh.length) exhaustedRef.current = true;
      }
      if (!fresh.length && currentDeck.length) {
        // spaced re-review: shuffle a copy of what exists and go again
        fresh = [...currentDeck].sort(() => Math.random() - 0.5).slice(0, 6)
          .map((c) => ({ ...c, review: true }));
      }
      if (fresh.length) setDeck((d) => [...(d || []), ...fresh]);
    } catch { /* background — next approach retries */ }
    finally { fetchingRef.current = false; }
  }, [cids]);

  useEffect(() => {
    if (!cids.length) { setDeck([]); return undefined; }
    // Pre-generated deck from the session cache = instant; else build it live.
    const cached = sessionCache.peekAll(cids, 'flashcards');
    if (cached.length) { setDeck(cached); setI(0); setPhase('front'); setResults({}); return undefined; }
    let alive = true;
    setDeck(null); setErr(''); setI(0); setPhase('front'); setResults({});
    APIService.engineFlashcards({ conceptIds: cids, count: 8 })
      .then((res) => { if (alive) setDeck(res.cards || []); })
      .catch((e) => { if (alive) { setErr(e.message || 'Could not build the deck.'); setDeck([]); } });
    return () => { alive = false; };
  }, [cids]);

  const go = useCallback((delta) => {
    setDir(delta > 0 ? 'right' : 'left');
    setPhase('front');
    setI((p) => {
      const n = (deck && deck.length) ? deck.length : 1;
      const next = (p + delta + n) % n;
      // running low ahead of the cursor → top the deck up in the background
      if (deck && n - next <= 2) extend(deck);
      return next;
    });
  }, [deck, extend]);

  const jump = useCallback((to) => {
    setDir(to >= i ? 'right' : 'left');
    setPhase('front');
    setI(to);
    if (deck && deck.length - to <= 2) extend(deck);
  }, [i, deck, extend]);

  const flip = useCallback(() => setPhase((p) => (p === 'front' ? 'back' : 'front')), []);

  // "Got it" → good, "Review again" → again: logged exactly as before.
  const rate = useCallback((key) => {
    const card = deck[i];
    const c = (card.concept_ids && card.concept_ids.length)
      ? card.concept_ids : conceptsFromText('flashcards', [card.topic]);
    logFlashcard(c, key, { item_id: card.item_id || null, depth: card.depth || null });
    setResults((r) => ({ ...r, [i]: key === 'again' ? 'again' : 'got' }));
  }, [deck, i]);

  const toggleIn = (setter, storageKey, key) => setter((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    saveSet(storageKey, next);
    return next;
  });

  useEffect(() => {
    const onKey = (e) => {
      if (!deck || !deck.length) return;
      const t = e.target;
      // never steal keys from fields, or from a focused button's own Enter/Space
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      const onButton = t && (t.tagName === 'BUTTON' || t.getAttribute?.('role') === 'button');
      if (e.key === ' ' || e.key === 'Enter') {
        if (onButton) return;
        e.preventDefault();
        flip();
      } else if (e.key === 'ArrowRight') go(1);
      else if (e.key === 'ArrowLeft') go(-1);
      else if (phase === 'back' && e.key === '1') rate('again');
      else if (phase === 'back' && e.key === '2') rate('good');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [deck, phase, go, flip, rate]);

  const subjects = (config?.subjects || []).map((s) => s.name).filter(Boolean);
  const title = subjects.slice(0, 2).join(' · ') + (subjects.length > 2 ? ` +${subjects.length - 2}` : '');
  const displayName = profile?.display_name || profile?.name || '';

  const shell = (body) => (
    <div className="pu-home pu-flash">
      <div className="pu-page">
        <header className="pu-topbar">
          <HomeSearch entries={searchEntries}
            onPick={(e) => (e.kind === 'track' ? onOpenPath?.(e.id) : onOpenHubNode?.(e.id))} />
          <div className="pu-topbar-end">
            {onToggleTheme && <ThemeToggle theme={theme} onToggle={onToggleTheme} />}
            <button type="button" className="pu-avatar" onClick={onEditProfile}
              aria-label={displayName ? `${displayName} — profile` : 'Profile'}>
              {profile?.avatar ? <img src={profile.avatar} alt="" /> : <span>{(displayName || 'U').slice(0, 1).toUpperCase()}</span>}
            </button>
          </div>
        </header>
        {body}
      </div>
    </div>
  );

  if (!cids.length) {
    return <ModeEmpty title="No topics configured"
      body="Choose topics in Session Settings and a flashcard deck will be built from your Knowledge Hub."
      scene="flashcards" eyebrow="Spaced recall"
      onConfigure={onConfigure} />;
  }
  if (deck === null) {
    return shell(
      <section className="fx-loading" aria-live="polite">
        <p className="pu-kicker">Flashcards</p>
        <p className="pu-meta">Building your deck…</p>
        <Loading rows={4} />
      </section>,
    );
  }
  if (!deck.length) {
    return <ModeEmpty title="No flashcards yet"
      body={err || 'No Knowledge Hub content found for the selected topics. Add questions on the Knowledge Hub page.'}
      scene="flashcards" eyebrow="Deck builder"
      onConfigure={onConfigure} />;
  }

  const card = deck[i];
  const key = cardKey(card);
  const category = card.review ? 'Review' : pretty(card.tag || card.topic || (card.concept_ids && card.concept_ids[0]) || 'Concept');
  const [answerHead, explanation] = splitAnswer(card.answer);
  const done = Object.keys(results).length;
  const result = results[i];
  const minutes = Math.max(1, Math.round((deck.length - done) * 0.5));   // ~30s a card

  return shell(
    <div className="learn-split fx-split">
      {learnOpen && (
        <LearnPanel conceptId={(card.concept_ids && card.concept_ids[0]) || cids[0]} onClose={toggleLearn} />
      )}
      <div className="learn-main">
        {/* Header — compact; the topic lives here, not in the list */}
        <section className="fx-head" aria-labelledby="fx-title">
          {onBack && (
            <button type="button" className="pu-link fx-backlink" onClick={onBack}>
              <IconArrowLeft size={14} /> Back to Practice
            </button>
          )}
          <p className="pu-kicker">Flashcards</p>
          <h1 id="fx-title">{title || pretty(card.topic) || 'Flashcards'}</h1>
          <p className="fx-lede">Quickly revise key concepts with curated flashcards.</p>
          <div className="fx-meta">
            <span className="fx-pill"><IconClock size={14} /> About {minutes} min left</span>
            <button type="button" className={`pu-link fx-notes${learnOpen ? ' on' : ''}`} onClick={toggleLearn} aria-pressed={learnOpen}>
              {learnOpen ? 'Hide concept notes' : 'Concept notes'}
            </button>
          </div>
        </section>

        <div className={`fx-body${listOpen ? ' list-open' : ''}`}>
          {/* Workspace */}
          <section className="pu-panel fx-work" aria-label="Flashcard">
            <div className="fx-toolbar">
              <span className="fx-count">Card {i + 1} of {deck.length}</span>
              <span className="fx-count-bar"><Bar value={(i + 1) / deck.length} label={`Card ${i + 1} of ${deck.length}`} /></span>
              <label className="fx-check">
                <input type="checkbox" checked={review.has(key)} onChange={() => toggleIn(setReview, REVIEW_KEY, key)} />
                Mark for review
              </label>
              <button type="button" className={`fx-icon-btn${bookmarks.has(key) ? ' on' : ''}`}
                onClick={() => toggleIn(setBookmarks, BOOKMARK_KEY, key)}
                aria-pressed={bookmarks.has(key)} aria-label={bookmarks.has(key) ? 'Remove bookmark' : 'Bookmark this card'}
                title={bookmarks.has(key) ? 'Bookmarked' : 'Bookmark'}>
                <IconBookmark filled={bookmarks.has(key)} />
              </button>
            </div>

            <div className="fx-stage">
              <button type="button" className="fx-arrow" onClick={() => go(-1)} aria-label="Previous card"><IconArrowLeft size={18} /></button>

              <div className={`fx-card fx-in-${dir}`} key={i}>
                <div className={`fx-inner${phase === 'back' ? ' flipped' : ''}`}>
                  <div className="fx-face fx-front" role="button" tabIndex={phase === 'front' ? 0 : -1}
                    aria-hidden={phase !== 'front'} onClick={flip}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); flip(); } }}
                    aria-label="Flip card to reveal the answer">
                    <span className="fx-tag">{category}</span>
                    <div className="fx-front-body">
                      <p className="fx-prompt">Can you tell the answer of this?</p>
                      <p className="fx-q">{card.question}</p>
                    </div>
                    <span className="fx-hint"><IconHand /> Click to reveal the answer</span>
                  </div>
                  <div className="fx-face fx-back" aria-hidden={phase !== 'back'}>
                    <span className="fx-tag">Answer</span>
                    <p className="fx-a">{answerHead}</p>
                    {explanation && (
                      <div className="fx-section">
                        <h3>Explanation</h3>
                        <p>{explanation}</p>
                      </div>
                    )}
                    {card.why && <div className="fx-note"><b>Why this is important</b><p>{card.why}</p></div>}
                    {card.takeaway && <div className="fx-note"><b>Key takeaway</b><p>{card.takeaway}</p></div>}
                    <div className="fx-rate">
                      {result ? (
                        <span className={`fx-rated ${result}`} role="status">
                          {result === 'got' ? <><IconCheck size={14} /> Got it — marked as known</> : <><IconRefresh size={14} /> Marked to review again</>}
                        </span>
                      ) : <span className="fx-rate-q">Did you know it?</span>}
                      <span className="fx-rate-btns">
                        <button type="button" className={`pu-btn pu-btn-sm fx-again${result === 'again' ? ' on' : ''}`} tabIndex={phase === 'back' ? 0 : -1} onClick={(e) => { e.stopPropagation(); rate('again'); }}>Review again</button>
                        <button type="button" className={`pu-btn pu-btn-sm pu-btn-quiet${result === 'got' ? ' on' : ''}`} tabIndex={phase === 'back' ? 0 : -1} onClick={(e) => { e.stopPropagation(); rate('good'); }}><IconCheck size={14} /> Got it</button>
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <button type="button" className="fx-arrow" onClick={() => go(1)} aria-label="Next card"><IconArrowRight size={18} /></button>
            </div>

            <div className="fx-actions">
              <button type="button" className="pu-btn pu-btn-quiet fx-nav" onClick={() => go(-1)}>
                <IconArrowLeft size={15} /> Previous
              </button>
              <button type="button" className="pu-btn pu-btn-primary fx-flip" onClick={flip}>
                <IconRefresh size={15} /> Flip Card
              </button>
              <button type="button" className="pu-btn fx-nav fx-next" onClick={() => go(1)}>
                Next <IconArrowRight size={15} />
              </button>
            </div>
            <p className="fx-keys" aria-hidden="true">← / → move · Space flips · <kbd>1</kbd> Review again · <kbd>2</kbd> Got it</p>
          </section>

          {/* Questions list — right panel on desktop, drawer on small screens */}
          <QuestionList
            open={listOpen}
            setOpen={setListOpen}
            total={deck.length}
            done={done}
            current={i}
            onPick={jump}
            label="Card"
            items={deck.map((c, n) => {
              const r = results[n];
              return {
                key: `${cardKey(c)}-${n}`,
                title: c.question,
                state: r || null,
                sub: `${c.review ? 'Review' : pretty(c.tag || c.topic || 'Concept')}${r === 'again' ? ' · Review again' : ''}${bookmarks.has(cardKey(c)) ? ' · Bookmarked' : ''}${review.has(cardKey(c)) ? ' · Marked' : ''}`,
              };
            })}
          />
        </div>
      </div>
    </div>,
  );
};

export default FlashcardMode;
